import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Textarea, Modal } from '../../components';
import FileViewer from '../../components/FileViewer';
import {
  getCourseById,
  createCourse,
  updateCourse,
  uploadCourseFile,
  deleteCourseFile,
  presignCourseVideo,
  deleteCourseVideo,
  triggerTranscription,
  ModuleDoc,
} from '../../services/course-service';

// ---- Types ----
type ItemType = 'text' | 'pdf' | 'video' | 'quiz';
type CourseType = 'coursework' | 'complementary';

interface ItemDraft {
  localId: string;
  title: string;
  type: ItemType;
  order: number;
  textContent: string;
  fileUrl: string;
  fileKey: string;
  fileName: string;
  fileSize: number;
  videoUrl: string;
  duration: number;
  uploading: boolean;
  passingScore: number;
  transcriptionStatus?: string;
}

interface ModuleDraft {
  localId: string;
  title: string;
  description: string;
  order: number;
  items: ItemDraft[];
}

// ---- Helpers ----
let _counter = 0;
const uid = () => `local_${++_counter}_${Date.now()}`;

const newItem = (type: ItemType, order: number): ItemDraft => ({
  localId: uid(), title: '', type, order,
  textContent: '', fileUrl: '', fileKey: '', fileName: '', fileSize: 0, videoUrl: '', duration: 0, uploading: false,
  passingScore: 70, transcriptionStatus: undefined,
});

const CATEGORIES = ['Business', 'Technical', 'Design', 'Finance', 'Presentation'];
const LEVELS     = ['beginner', 'intermediate', 'advanced'];

// ---- Main Component ----
const CourseBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [meta, setMeta] = useState({ title: '', description: '', category: 'Business', level: 'beginner', courseType: 'coursework' as CourseType, price: '', thumbnailUrl: '', thumbnailKey: '' });
  const [modules, setModules]   = useState<ModuleDraft[]>([]);
  const [courseId, setCourseId] = useState<string | null>(id || null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError]       = useState('');
  const [preview, setPreview]   = useState<{ url: string; type: 'pdf' | 'video'; name: string } | null>(null);

  // Load on edit
  useEffect(() => {
    if (!id) return;
    getCourseById(id).then(course => {
      setMeta({ title: course.title, description: course.description, category: course.category, level: course.level, courseType: (course.courseType || 'coursework') as CourseType, price: course.price || '', thumbnailUrl: course.thumbnailUrl, thumbnailKey: course.thumbnailKey });
      setModules(course.modules.map(m => ({
        localId: uid(), title: m.title, description: m.description, order: m.order,
        items: m.items.map(it => ({
          localId: uid(), title: it.title, type: it.type, order: it.order,
          textContent: it.textContent, fileUrl: it.fileUrl, fileKey: it.fileKey,
          fileName: it.fileName, fileSize: it.fileSize, videoUrl: it.videoUrl || '', duration: it.duration, uploading: false,
          transcriptionStatus: (it as any).transcriptionStatus || undefined,
          passingScore: it.passingScore ?? 70,
        })),
      })));
    }).catch(e => setError(e.message));
  }, [id]);

  // Ensure course exists (auto-create for file uploads)
  const ensureCourseId = useCallback(async (): Promise<string> => {
    if (courseId) return courseId;
    if (!meta.title.trim()) throw new Error('Enter a course title before uploading files.');
    const c = await createCourse({ title: meta.title, category: meta.category, description: meta.description, level: meta.level, courseType: meta.courseType, price: meta.price });
    setCourseId(c._id);
    return c._id;
  }, [courseId, meta]);

  // ---- Module ops ----
  const addModule = () =>
    setModules(p => [...p, { localId: uid(), title: 'New Module', description: '', order: p.length, items: [] }]);

  const patchModule = (localId: string, patch: Partial<Pick<ModuleDraft, 'title' | 'description'>>) =>
    setModules(p => p.map(m => m.localId === localId ? { ...m, ...patch } : m));

  const moveModule = (localId: string, dir: -1 | 1) =>
    setModules(p => {
      const a = [...p]; const i = a.findIndex(m => m.localId === localId); const t = i + dir;
      if (t < 0 || t >= a.length) return a;
      [a[i], a[t]] = [a[t], a[i]];
      return a.map((m, idx) => ({ ...m, order: idx }));
    });

  const deleteModule = async (localId: string) => {
    if (!window.confirm('Delete this module and all its content?')) return;
    const mod = modules.find(m => m.localId === localId);
    if (mod && courseId) {
      await Promise.all(mod.items.filter(it => it.fileKey).map(it => deleteCourseFile(courseId, it.fileKey).catch(() => {})));
    }
    setModules(p => p.filter(m => m.localId !== localId));
  };

  // ---- Item ops ----
  const addItem = (modId: string, type: ItemType) =>
    setModules(p => p.map(m => m.localId !== modId ? m : { ...m, items: [...m.items, newItem(type, m.items.length)] }));

  const patchItem = (modId: string, itemId: string, patch: Partial<ItemDraft>) =>
    setModules(p => p.map(m => m.localId !== modId ? m : { ...m, items: m.items.map(it => it.localId !== itemId ? it : { ...it, ...patch }) }));

  const moveItem = (modId: string, itemId: string, dir: -1 | 1) =>
    setModules(p => p.map(m => {
      if (m.localId !== modId) return m;
      const a = [...m.items]; const i = a.findIndex(it => it.localId === itemId); const t = i + dir;
      if (t < 0 || t >= a.length) return m;
      [a[i], a[t]] = [a[t], a[i]];
      return { ...m, items: a.map((it, idx) => ({ ...it, order: idx })) };
    }));

  const deleteItem = async (modId: string, itemId: string) => {
    const mod = modules.find(m => m.localId === modId);
    const item = mod?.items.find(it => it.localId === itemId);
    if (item?.fileKey && courseId) await deleteCourseFile(courseId, item.fileKey).catch(() => {});
    setModules(p => p.map(m => m.localId !== modId ? m : { ...m, items: m.items.filter(it => it.localId !== itemId) }));
  };

  const handleUpload = useCallback(async (modId: string, itemId: string, file: File) => {
    patchItem(modId, itemId, { uploading: true });
    try {
      const cid = await ensureCourseId();
      const r = await uploadCourseFile(cid, file);
      patchItem(modId, itemId, { fileUrl: r.fileUrl, fileKey: r.fileKey, fileName: r.fileName, fileSize: r.fileSize, uploading: false });
    } catch (e: any) {
      patchItem(modId, itemId, { uploading: false });
      alert(e.message);
    }
  }, [ensureCourseId]);

  const handleVideoUpload = useCallback(async (modId: string, itemId: string, file: File, existingFileKey?: string) => {
    if (existingFileKey) deleteCourseVideo(existingFileKey).catch(() => {});
    patchItem(modId, itemId, { uploading: true });
    try {
      await ensureCourseId();
      const { presignedUrl, fileKey, fileUrl } = await presignCourseVideo(file.name, file.type || 'video/mp4');
      const up = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'video/mp4' },
        body: file,
      });
      if (!up.ok) throw new Error('Upload to storage failed');
      patchItem(modId, itemId, { fileKey, fileUrl, fileName: file.name, fileSize: file.size, uploading: false });
    } catch (e: any) {
      patchItem(modId, itemId, { uploading: false });
      alert(e.message);
    }
  }, [ensureCourseId]);

  // ---- Save ----
  const serialize = (): ModuleDoc[] => {
    const counts: Record<string, number> = { text: 0, pdf: 0, video: 0, quiz: 0 };
    const typeLabel: Record<string, string> = { text: 'Reading', pdf: 'Document', video: 'Video', quiz: 'Quiz' };
    return modules.map((m, mi) => ({
      title: m.title.trim() || `Module ${mi + 1}`,
      description: m.description,
      order: m.order,
      items: m.items.map(({ localId: _l, uploading: _u, transcriptionStatus: _ts, ...rest }) => {
        counts[rest.type] = (counts[rest.type] || 0) + 1;
        const title = rest.title.trim() || `${typeLabel[rest.type]} ${counts[rest.type]}`;
        return { ...rest, title, videoUrl: rest.videoUrl || '', passingScore: rest.passingScore, questions: [] };
      }),
    }));
  };

  const save = async (status: 'draft' | 'published') => {
    if (!meta.title.trim()) { setError('Title is required.'); return; }
    setError(''); setIsSaving(true);
    try {
      let cid = courseId;
      if (!cid) {
        const c = await createCourse({ title: meta.title, category: meta.category, description: meta.description, level: meta.level, courseType: meta.courseType, price: meta.price });
        cid = c._id; setCourseId(cid);
      }
      const savedCourse = await updateCourse(cid, { ...meta, status, modules: serialize() });
      for (const mod of savedCourse.modules) {
        for (const item of mod.items) {
          if (item.type === 'video' && item.fileKey && (item as any).transcriptionStatus === 'none') {
            triggerTranscription(cid, mod._id!, item._id!).catch(() => {});
          }
        }
      }
      navigate('/app/admin/courses');
    } catch (e: any) { setError(e.message); }
    finally { setIsSaving(false); }
  };

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate('/app/admin/courses')} className="text-muted text-sm hover:text-text mb-1 flex items-center gap-1">
            ← Back to Courses
          </button>
          <h1 className="text-3xl font-bold text-text">{isEdit ? 'Edit Course' : 'New Course'}</h1>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={() => save('draft')} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button variant="primary" onClick={() => save('published')} disabled={isSaving}>
            Publish
          </Button>
        </div>
      </div>

      {error && <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">{error}</div>}

      {/* Metadata */}
      <section>
        <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">Course Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input label="Course Title *" value={meta.title} onChange={e => setMeta(m => ({ ...m, title: e.target.value }))} placeholder="e.g. Introduction to Business Finance" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-text mb-2">Description</label>
            <Textarea value={meta.description} onChange={e => setMeta(m => ({ ...m, description: e.target.value }))} placeholder="What will learners achieve?" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Category *</label>
            <select value={meta.category} onChange={e => setMeta(m => ({ ...m, category: e.target.value }))} className="w-full bg-surface border border-line rounded-lg px-3 py-2 text-text">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Level</label>
            <select value={meta.level} onChange={e => setMeta(m => ({ ...m, level: e.target.value }))} className="w-full bg-surface border border-line rounded-lg px-3 py-2 text-text capitalize">
              {LEVELS.map(l => <option key={l} value={l} className="capitalize">{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Course Type</label>
            <select value={meta.courseType} onChange={e => setMeta(m => ({ ...m, courseType: e.target.value as CourseType }))} className="w-full bg-surface border border-line rounded-lg px-3 py-2 text-text">
              <option value="coursework">Coursework</option>
              <option value="complementary">Complementary</option>
            </select>
          </div>
          {meta.courseType === 'complementary' && (
            <div>
              <Input label="Price" value={meta.price} onChange={e => setMeta(m => ({ ...m, price: e.target.value }))} placeholder="e.g. Free or $299" />
            </div>
          )}
        </div>
      </section>

      {/* Modules */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-text font-semibold text-lg">Modules</h2>
          <Button variant="ghost" size="sm" onClick={addModule}>+ Add Module</Button>
        </div>

        {modules.length === 0 && (
          <p className="text-muted text-sm py-6">No modules yet. Click "Add Module" to start.</p>
        )}

        <div className="space-y-6">
          {modules.map((mod, mi) => (
            <div key={mod.localId} className="border border-line/40 rounded-lg p-4 bg-surface/20">
              {/* Module header */}
              <div className="flex gap-3 mb-4 items-start">
                <div className="flex flex-col gap-0.5 pt-8">
                  <button onClick={() => moveModule(mod.localId, -1)} disabled={mi === 0} className="text-muted text-xs hover:text-text disabled:opacity-30">▲</button>
                  <button onClick={() => moveModule(mod.localId, 1)} disabled={mi === modules.length - 1} className="text-muted text-xs hover:text-text disabled:opacity-30">▼</button>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label={`Module ${mi + 1} Title`} value={mod.title} onChange={e => patchModule(mod.localId, { title: e.target.value })} />
                  <Input label="Description (optional)" value={mod.description} onChange={e => patchModule(mod.localId, { description: e.target.value })} />
                </div>
                <button onClick={() => deleteModule(mod.localId)} className="text-danger hover:text-danger/70 text-sm pt-8">Remove</button>
              </div>

              {/* Items */}
              <div className="ml-8 space-y-3">
                {mod.items.map((item, ii) => (
                  <ItemRow
                    key={item.localId}
                    item={item}
                    index={ii}
                    total={mod.items.length}
                    onMove={dir => moveItem(mod.localId, item.localId, dir)}
                    onDelete={() => deleteItem(mod.localId, item.localId)}
                    onPatch={patch => patchItem(mod.localId, item.localId, patch)}
                    onUpload={file => handleUpload(mod.localId, item.localId, file)}
                    onVideoUpload={file => handleVideoUpload(mod.localId, item.localId, file, item.fileKey || undefined)}
                    onPreview={(url, type, name) => setPreview({ url, type, name })}
                  />
                ))}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-line/50">
                  <span className="text-muted text-xs self-center">Add:</span>
                  {(['text', 'pdf', 'video', 'quiz'] as ItemType[]).map(t => (
                    <button key={t} onClick={() => addItem(mod.localId, t)}
                      className="px-2 py-1 bg-surface border border-line rounded text-muted text-xs hover:text-text hover:border-chart transition-colors capitalize">
                      + {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <Modal isOpen title={preview.name} onClose={() => setPreview(null)} className="w-[70vw] max-w-none">
          <FileViewer
            url={preview.url}
            type={preview.type}
            fileName={preview.name}
            className={preview.type === 'video' ? 'h-[60vh]' : ''}
          />
        </Modal>
      )}
    </div>
  );
};

// ---- ItemRow ----
interface ItemRowProps {
  item: ItemDraft;
  index: number;
  total: number;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
  onPatch: (patch: Partial<ItemDraft>) => void;
  onUpload: (file: File) => void;
  onVideoUpload: (file: File) => void;
  onPreview: (url: string, type: 'pdf' | 'video', name: string) => void;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, index, total, onMove, onDelete, onPatch, onUpload, onVideoUpload, onPreview }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);

  const TYPE_LABEL: Record<ItemType, string> = { text: 'Text', pdf: 'PDF', video: 'Video', quiz: 'Quiz' };

  return (
    <div className="border border-line/60 rounded-lg p-3 bg-bg/30">
      <div className="flex gap-2 items-start">
        {/* Order arrows */}
        <div className="flex flex-col gap-0.5 pt-1 flex-shrink-0">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="text-muted text-xs hover:text-text disabled:opacity-30">▲</button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="text-muted text-xs hover:text-text disabled:opacity-30">▼</button>
        </div>

        <div className="flex-1 space-y-3">
          {/* Title row */}
          <div className="flex items-center gap-2">
            <span className="text-xs px-1.5 py-0.5 bg-surface border border-line rounded text-muted capitalize flex-shrink-0">
              {TYPE_LABEL[item.type]}
            </span>
            <Input placeholder="Item title" value={item.title} onChange={e => onPatch({ title: e.target.value })} />
          </div>

          {/* Content area */}
          {item.type === 'text' && (
            <Textarea placeholder="Write your text content here…" value={item.textContent} onChange={e => onPatch({ textContent: e.target.value })} rows={4} />
          )}

          {item.type === 'pdf' && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }}
              />
              {item.fileUrl ? (
                <div className="flex items-center gap-3 p-3 bg-surface border border-line rounded-lg text-sm">
                  <svg className="w-4 h-4 text-success flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span className="text-text truncate flex-1">{item.fileName}</span>
                  <button onClick={() => onPreview(item.fileUrl, 'pdf', item.fileName)} className="text-chart text-xs hover:underline flex-shrink-0">View</button>
                  <button onClick={() => fileRef.current?.click()} className="text-muted text-xs hover:text-text flex-shrink-0">Replace</button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={item.uploading}
                  className="w-full p-4 border border-dashed border-line rounded-lg text-muted text-sm hover:border-chart hover:text-text transition-colors"
                >
                  {item.uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-chart border-t-transparent rounded-full animate-spin" />
                      Uploading…
                    </span>
                  ) : (
                    'Click to upload PDF'
                  )}
                </button>
              )}
            </>
          )}

          {item.type === 'video' && (
            <>
              <input
                ref={videoRef}
                type="file"
                accept="video/mp4"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) onVideoUpload(f); e.target.value = ''; }}
              />
              {item.fileUrl ? (
                <div className="flex items-center gap-3 p-3 bg-surface border border-line rounded-lg text-sm">
                  <svg className="w-4 h-4 text-success flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span className="text-text truncate flex-1">{item.fileName}</span>
                  {item.transcriptionStatus === 'pending' && (
                    <span className="text-xs text-warning bg-warning/10 border border-warning/20 px-2 py-0.5 rounded-full flex-shrink-0">Transcribing…</span>
                  )}
                  {item.transcriptionStatus === 'done' && (
                    <span className="text-xs text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded-full flex-shrink-0">Transcribed</span>
                  )}
                  {item.transcriptionStatus === 'failed' && (
                    <span className="text-xs text-danger bg-danger/10 border border-danger/20 px-2 py-0.5 rounded-full flex-shrink-0">Transcription failed</span>
                  )}
                  {item.transcriptionStatus === 'skipped_too_large' && (
                    <span className="text-xs text-muted bg-surface border border-line px-2 py-0.5 rounded-full flex-shrink-0">&gt;25 MB — skipped</span>
                  )}
                  <button onClick={() => onPreview(item.fileUrl, 'video', item.fileName)} className="text-chart text-xs hover:underline flex-shrink-0">Preview</button>
                  <button onClick={() => videoRef.current?.click()} className="text-muted text-xs hover:text-text flex-shrink-0">Replace</button>
                </div>
              ) : (
                <button
                  onClick={() => videoRef.current?.click()}
                  disabled={item.uploading}
                  className="w-full p-4 border border-dashed border-line rounded-lg text-muted text-sm hover:border-chart hover:text-text transition-colors"
                >
                  {item.uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-chart border-t-transparent rounded-full animate-spin" />
                      Uploading…
                    </span>
                  ) : (
                    'Click to upload MP4'
                  )}
                </button>
              )}
            </>
          )}

          {item.type === 'quiz' && (
            <div className="flex items-center gap-3">
              <span className="text-muted text-sm">Passing score:</span>
              <input
                type="number" min={0} max={100} value={item.passingScore}
                onChange={e => onPatch({ passingScore: Number(e.target.value) })}
                className="w-16 bg-surface border border-line rounded px-2 py-1 text-text text-sm"
              />
              <span className="text-muted text-sm">%</span>
            </div>
          )}
        </div>

        <button onClick={onDelete} className="text-danger hover:text-danger/70 text-sm pt-1 flex-shrink-0">✕</button>
      </div>
    </div>
  );
};

export default CourseBuilder;
